/**
 * studio-app-route-placement — the studio's app-tier ROUTE re-home rewrite, and
 * the transport discriminant, shared by BOTH the full `studioAppPlugin` and the
 * schema-free `studioAppRoutePlugin` (#705 S2).
 *
 * A generated cluster's RAW routes (the `route/home` index at `/` + one
 * `route/{node}` per node at `/{plural}`) are re-placed into the studio's URL
 * space by a pure rewrite: `omitIndex` drops the redundant single-node index
 * route; `routePrefix` prefixes EVERY route (the index `/` BECOMES the prefix);
 * otherwise only the `/` index is re-homed to `appHomePath` (default `/app`) so
 * the shell landing surface owns `/`. Extracting the rewrite here (out of
 * `studio-app-plugin.tsx`) lets the route-only plugin apply the IDENTICAL
 * placement to `projectRouteEntities`'s output — so a cluster's eager route
 * table is byte-identical whether it comes from the full `generate()` graph or
 * the schema-free descriptor projection.
 *
 * PURE + schema-free: no React, no `generate()`, no model schema. Same entities +
 * placement ⇒ byte-identical output.
 *
 * @module @ubiclouds/v5-studio/genome/studio-app-route-placement
 */

import type { EntityDef } from '@ubiclouds/v5-sdk/genome';

/** Default sub-path the generated app's index route is re-homed to. */
export const DEFAULT_APP_HOME_PATH = '/app';

/**
 * Which data transport a generated cluster's editors read through (#S-18b). The
 * discriminant is a STRING resolved INSIDE the deferred editor chunk, so the
 * live-seam / localStorage-seam modules never enter the studio entry.
 */
export type StudioAppTransport =
  | 'mock'
  | 'host-app-live'
  | 'agent-profiles-local'
  | 'slicer-profile-local'
  | 'filament-local'
  | 'monzo-fixture'
  | 'home-battery-local';

/** The route-placement knobs — how a cluster's RAW routes land in the studio URL space. */
export interface RoutePlacement {
  /** Sub-path to re-home the generated `/` index route to (default {@link DEFAULT_APP_HOME_PATH}). */
  readonly appHomePath?: string;
  /**
   * Prefix EVERY generated route path with this base (the index `/` BECOMES the
   * prefix; `/hosts` → `${prefix}/hosts`), instead of only re-homing the index.
   * Takes precedence over `appHomePath`.
   */
  readonly routePrefix?: string;
  /**
   * Drop the generated `/` index ROUTE entity entirely (#363a) — a single-node
   * cluster's index is a redundant one-link page. The index COMPONENT stays in the
   * graph (a routeless component is valid); only its route entity is filtered out.
   * Cannot combine with `routePrefix` (which prefixes the index into the entry path).
   */
  readonly omitIndex?: boolean;
}

/**
 * Apply the studio route-placement rewrite to a mixed entity list (routes +
 * non-routes; non-routes pass through unchanged). Extracted verbatim from the
 * former `studioAppPlugin.entities()` body so BOTH plugins re-home identically.
 */
export function applyRoutePlacement(
  entities: readonly EntityDef[],
  placement: RoutePlacement,
): EntityDef[] {
  const appHomePath = placement.appHomePath ?? DEFAULT_APP_HOME_PATH;
  const { routePrefix, omitIndex = false } = placement;
  return entities
    // omitIndex (#363a): drop the redundant `/` index ROUTE entity (the index
    // COMPONENT stays — a routeless component is valid). For a single-node model
    // the node-list route IS the user-facing surface.
    .filter((entity) => !(omitIndex && entity.kind === 'route' && entity.meta?.['path'] === '/'))
    .map((entity) => {
      if (entity.kind !== 'route') return entity;
      const path = entity.meta?.['path'];
      if (typeof path !== 'string') return entity;
      // routePrefix: prefix EVERY route (the index `/` becomes the prefix).
      if (routePrefix != null) {
        const next = path === '/' ? routePrefix : `${routePrefix}${path}`;
        return { ...entity, meta: { ...entity.meta, path: next } };
      }
      // default: re-home only the `/` index to appHomePath.
      if (path !== '/') return entity;
      return { ...entity, meta: { ...entity.meta, path: appHomePath } };
    });
}
